/**
 * 流程配置 API 服务
 * 调用 axiom-agent-admin 服务
 */

import { ApiClient, apiClients } from '@/shared/services/api'
import type { SuccessResponse } from '@/shared/types/api'
import type {
  FlowConfig,
  FlowValidateRequest,
  FlowValidateResponse,
} from '@/modules/agent/types/flow'

/**
 * 流程配置 API 服务
 */
export class FlowApiService {
  /**
   * 获取流程配置
   */
  static async getFlowConfig(
    projectId: string
  ): Promise<SuccessResponse<FlowConfig>> {
    console.log('📥 [FlowApiService] 请求flow配置:', { projectId })
    
    const response = await ApiClient.getWithClient<FlowConfig>(
      apiClients.agentAdmin,
      `/flow/projects/${projectId}/flow`
    )
    
    const edgesData = response.data?.edges || []
    console.log('📥 [FlowApiService] flow配置响应:')
    console.log('  success:', response.success)
    console.log('  nodesCount:', response.data?.nodes?.length || 0)
    console.log('  edgesCount:', edgesData.length)
    console.log('  rawEdges:', JSON.stringify(edgesData, null, 2))
    console.log('  edges summary:', JSON.stringify(edgesData.map((e: any) => ({ 
      id: e.id, 
      source: e.source, 
      target: e.target,
      sourceHandle: e.sourceHandle,
      targetHandle: e.targetHandle,
      markerEnd: e.markerEnd,
      style: e.style,
    })), null, 2))
    
    return response
  }

  /**
   * 保存流程配置（草稿版本）
   */
  static async saveFlowConfig(
    projectId: string,
    config: { nodes: FlowConfig['nodes']; edges: FlowConfig['edges']; change_summary?: string }
  ): Promise<SuccessResponse<{
    version: number
    updated_at: string
    validation: {
      is_valid: boolean
      warnings: Array<{ node_id?: string; message: string }>
      errors: Array<{ node_id?: string; message: string; code?: string }>
    }
  }>> {
    console.log('📤 [FlowApiService] 发送保存请求:', {
      projectId,
      nodesCount: config.nodes.length,
      edgesCount: config.edges.length,
      nodes: JSON.parse(JSON.stringify(config.nodes.map(n => ({ id: n.id, type: n.type })))),
      edges: JSON.parse(JSON.stringify(config.edges.map(e => ({ 
        id: e.id, 
        source: e.source, 
        target: e.target,
        sourceHandle: e.sourceHandle,
        markerEnd: e.markerEnd,
        style: e.style,
      })))),
      fullEdges: JSON.parse(JSON.stringify(config.edges)),
      requestBody: {
        nodes: config.nodes,
        edges: config.edges,
        change_summary: config.change_summary,
      },
    })
    
    const response = await ApiClient.putWithClient<{
      version: number
      updated_at: string
      validation: {
        is_valid: boolean
        warnings: Array<{ node_id?: string; message: string }>
        errors: Array<{ node_id?: string; message: string; code?: string }>
      }
    }>(
      apiClients.agentAdmin,
      `/flow/projects/${projectId}/flow`,
      {
        nodes: config.nodes,
        edges: config.edges,
        change_summary: config.change_summary,
      }
    )
    
    console.log('📥 [FlowApiService] 保存响应:')
    console.log('  success:', response.success)
    console.log('  data:', JSON.stringify(response.data, null, 2))
    
    return response
  }

  /**
   * 获取流程版本列表
   */
  static async getFlowVersions(
    projectId: string
  ): Promise<SuccessResponse<{
    versions: Array<{
      version: string
      published: boolean
      published_at: string
      published_by: string
    }>
  }>> {
    return ApiClient.getWithClient<{
      versions: Array<{
        version: string
        published: boolean
        published_at: string
        published_by: string
      }>
    }>(
      apiClients.agentAdmin,
      `/projects/${projectId}/flow/versions`
    )
  }

  /**
   * 发布流程配置
   */
  static async publishFlow(
    projectId: string,
    version: number | null = null
  ): Promise<SuccessResponse<{
    project_id: string
    version: number
    nodes: unknown[]
    edges: unknown[]
    is_published: boolean
    updated_at: string
    updated_by: string
    change_summary: string
  }>> {
    console.log('🚀 [FlowApiService] 发布项目:', { projectId, version })
    
    const response = await ApiClient.postWithClient<{
      project_id: string
      version: number
      nodes: unknown[]
      edges: unknown[]
      is_published: boolean
      updated_at: string
      updated_by: string
      change_summary: string
    }>(
      apiClients.agentAdmin,
      `/projects/${projectId}/flow/publish`,
      { version }  // 添加请求体，version 为 null 表示发布当前版本
    )
    
    console.log('✅ [FlowApiService] 发布响应:', JSON.stringify(response, null, 2))
    return response
  }

  /**
   * 验证流程配置
   */
  static async validateFlow(
    request: FlowValidateRequest
  ): Promise<SuccessResponse<FlowValidateResponse>> {
    return ApiClient.postWithClient<FlowValidateResponse>(
      apiClients.agentAdmin,
      '/flow/validate',
      request
    )
  }
}

